<template>
  <b-modal :active.sync="isModalActive" :width="980" scroll="keep" :can-cancel="[]">
    <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false"></b-loading>

    <div class="flex flex-col max-h-[85vh] rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg">
      <!-- HEADER (fijo) -->
      <header class="flex-none flex justify-between items-center px-5 py-3.5 border-b border-[#E4E6EB] bg-white">
        <h2 class="text-[17px] font-semibold text-gray-900 min-w-0 break-words">
          {{ $t('configure_subdivisions_title') }}
        </h2>
        <button
          v-if="!processingData"
          @click="hide()"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-[#f2f2f7] hover:text-[#3a6bc8] transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-[#c9d6ef] focus:ring-offset-1"
          aria-label="Cerrar"
        >
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </header>

      <!-- BODY (scrollable) — dos columnas: formulario | resumen -->
      <section class="flex-1 overflow-y-auto px-5 py-4 bg-white">
        <!-- FILA 1: switches globales (tipos de subdivisión + por capa). -->
        <div class="flex flex-row flex-wrap items-center gap-x-6 gap-y-2 mb-4 pb-4 border-b border-[#eef0f4]">
          <b-field class="mb-0">
            <b-switch v-model="createElements.pilings" :rounded="true" :outlined="false"
              size="is-small" :left-label="true"
              class="[&_input:checked+.check]:!bg-[#3a6bc8]">
              {{ $t('create') }} {{ $t('pilings') }}
            </b-switch>
          </b-field>
          <b-field class="mb-0">
            <b-switch v-model="createElements.modules" :rounded="true" :outlined="false"
              size="is-small" :left-label="true"
              class="[&_input:checked+.check]:!bg-[#3a6bc8]">
              {{ $t('create') }} {{ $t('modules') }}
            </b-switch>
          </b-field>
          <b-field class="mb-0">
            <b-switch v-model="layeredDesign" :rounded="true" :outlined="false"
              size="is-small" :left-label="true"
              class="[&_input:checked+.check]:!bg-[#3a6bc8]">
              {{ $t('subdivide_by_layer') }}
            </b-switch>
          </b-field>
        </div>

        <!-- FILA 2: formulario (más ancho) | resumen (más estrecho). -->
        <div class="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-x-6 gap-y-4">

          <!-- ───────── COLUMNA 1: formulario de configuración ───────── -->
          <div class="space-y-3">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{{ $t('configuration') }}</p>
            <!-- Formulario: General siempre; "Por capas" solo si layeredDesign. -->
            <b-tabs v-model="activeTab" type="is-boxed" size="is-small" :animated="false">
              <b-tab-item :label="$t('general')" icon="cog" icon-pack="fas">
                <fields-design-form ref="frm_default"
                  :disable-fields="processingData"
                  :create-elements="createElements"
                  :structure-count="consideredStructures"
                  @epc-change="onDesignFormChange" />
              </b-tab-item>

              <b-tab-item v-if="layeredDesign" :label="$t('by_layer')" icon="layer-group" icon-pack="fas">
                <layers-design-list ref="refLayersDesign"
                  :layers="layers"
                  :create-elements="createElements"
                  :disable-fields="processingData"
                  :structures-by-layer="structuresByLayer"
                  :initial-design="layerConfigs"
                  @epc-change="onLayersConfigChange" />
              </b-tab-item>
            </b-tabs>
          </div>

          <!-- ───────── COLUMNA 2: resumen ───────── -->
          <div class="space-y-3">
            <p class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{{ $t('summary') }}</p>

            <!-- Total de estructuras (base, viene del DXF) con badge en línea. -->
            <div class="rounded-lg border border-[#c9d6ef] bg-[#eef3fb] px-4 py-3">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-medium text-[#2f5aad] flex items-center gap-1.5">
                  <b-icon pack="fas" icon="stream" size="is-small" />
                  {{ $t('total_structures') }}
                </span>
                <span class="inline-flex items-center gap-1 text-[10px] font-medium text-[#3a6bc8] bg-white/70 rounded-full px-2 py-0.5">
                  <b-icon pack="fas" icon="vector-square" size="is-small" />
                  {{ $t('calculated_from_dxf') }}
                </span>
                <span class="ml-auto text-xl font-bold text-[#2f5aad] tabular-nums">{{ consideredStructures }}</span>
              </div>
            </div>

            <!-- Desglose general estimado (ejes/hincados/módulos/paneles) en tiles. -->
            <div class="grid grid-cols-2 gap-2">
              <div v-for="row in summaryRows" :key="row.key"
                class="rounded-lg border px-3 py-2 transition-colors"
                :class="row.disabled ? 'opacity-50 bg-gray-50 border-[#e9e9ef]' : 'bg-white border-[#e9e9ef]'">
                <p class="text-[10px] font-semibold uppercase tracking-wide text-gray-400 truncate">{{ row.label }}</p>
                <p class="text-lg font-bold text-gray-900 tabular-nums leading-tight">{{ row.value }}</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <!-- FOOTER (fijo) -->
      <footer class="flex-none px-5 py-3.5 border-t border-[#E4E6EB] bg-white">
        <div class="w-full flex items-center gap-2" :class="processingData ? 'justify-center' : 'justify-end'">
          <template v-if="!processingData">
            <button type="button" @click="hide()"
              class="px-3.5 py-1.5 rounded-lg text-sm font-medium text-gray-600 bg-white border border-[#e9e9ef] hover:bg-[#f2f2f7] transition-colors">
              {{ $t('close') }}
            </button>
            <button type="button" @click="handleClick()"
              class="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-sm font-medium text-white bg-[#3a6bc8] hover:bg-[#2f5aad] transition-colors">
              <b-icon pack="fas" icon="check" size="is-small" />
              {{ $t('accept') }}
            </button>
          </template>
          <v-button-processing v-else />
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import FieldsDesignForm from "../../../components/fields-design-form.vue"
import LayersDesignList from "../../../components/layers-design-list.vue"

export default {
  name: "ConfigureSubdivisionsModal",

  components: {
    FieldsDesignForm,
    LayersDesignList,
  },

  props: {},

  data() {
    return {
      isModalActive: false,
      isLoading: false,
      processingData: false,

      // Pestaña del FORMULARIO (col 1): 0 = General, 1 = Por capas
      activeTab: 0,
      // Si el switch está activo, se muestra el tab "Por capas" y se procesan los overrides
      layeredDesign: false,
      // Capas del DXF (extraídas de cadJson.tables.layer.layers en show())
      layers: [],
      // Total de estructuras detectadas en el DXF (POLYLINE/LWPOLYLINE)
      totalStructures: 0,
      // Conteo de estructuras por capa (del DXF) para el resumen "Por capa".
      structuresByLayer: [],
      // Qué tipos de entidad debe generar el procesador
      createElements: {
        pilings: false,
        modules: false,
      },
      // Espejo de los valores del formulario General para el resumen en vivo
      // (estructuras × config). Lo actualiza fields-design-form vía @epc-change.
      designForm: {
        total_axles:   1,
        total_pilings: 0,
        total_modules: 0,
        total_panels:  1,
      },
      // Config VALIDADA por capa (key = nombre de capa saneado) que emite
      // layers-design-list; una capa sin config usa la general (designForm).
      layerConfigs: {},
    }
  },

  computed: {
    /**
     * Capas consideradas para el resumen: con elementos (count > 0) y que NO sean
     * la capa "0" (capa base del DXF, no participa).
     */
    consideredLayers() {
      return this.structuresByLayer.filter(r => r.count > 0 && r.layer !== '0')
    },

    /** Total de estructuras consideradas (suma de las capas válidas). */
    consideredStructures() {
      return this.consideredLayers.reduce((acc, r) => acc + r.count, 0)
    },

    /**
     * Totales del resumen: se SUMA capa por capa usando la config efectiva de cada
     * una — su config por capa VALIDADA si existe (layeredDesign), o la general
     * (designForm) por defecto. Capas sin elementos o "0" no cuentan.
     */
    summaryTotals() {
      let axles = 0, pilings = 0, modules = 0, panels = 0
      for (const r of this.consideredLayers) {
        const f = this._EffectiveConfig(r.layer)
        axles += r.count * (Number(f.total_axles) || 0)
        // Hincados = estructuras × ejes × hincados por eje.
        if (this.createElements.pilings) pilings += r.count * (Number(f.total_axles) || 0) * (Number(f.total_pilings) || 0)
        if (this.createElements.modules) {
          const m = r.count * (Number(f.total_modules) || 0)
          modules += m
          panels += m * (Number(f.total_panels) || 0)
        }
      }
      return { axles, pilings, modules, panels }
    },

    /** Filas del resumen general a partir de summaryTotals. */
    summaryRows() {
      const t = this.summaryTotals
      return [
        { key: 'axles',   label: this.$t('total_axles'),   value: t.axles },
        { key: 'pilings', label: this.$t('total_pilings'), value: t.pilings, disabled: !this.createElements.pilings },
        { key: 'modules', label: this.$t('total_modules'), value: t.modules, disabled: !this.createElements.modules },
        { key: 'panels',  label: this.$t('total_panels'),  value: t.panels,  disabled: !this.createElements.modules },
      ]
    },

  },

  watch: {
    // Si el usuario apaga el switch estando en el tab "Por capas" del formulario,
    // vuelve a "General" (si no, b-tabs apuntaría a un tab eliminado).
    layeredDesign(active) {
      if (!active && this.activeTab !== 0) this.activeTab = 0
    },
  },

  methods: {
    /** Espejo reactivo de los valores del formulario General (resumen en vivo). */
    onDesignFormChange(form) {
      this.designForm = { ...this.designForm, ...form }
    },

    /** Config VALIDADA por capa (key saneado = nombre con espacios → "_"). */
    onLayersConfigChange(map) {
      this.layerConfigs = map || {}
    },

    /** Saneado de nombre de capa igual que layers-design-list (clave de config). */
    _Sanitize(name) {
      return String(name || '').replace(/\s/g, '_')
    },

    /**
     * Config efectiva de una capa: su config por capa validada (si hay diseño por
     * capas y existe), o la general por defecto.
     */
    _EffectiveConfig(layerName) {
      if (this.layeredDesign) {
        const cfg = this.layerConfigs[this._Sanitize(layerName)]
        if (cfg) return cfg
      }
      return this.designForm
    },

    /**
     * Valida los formularios y emite la configuración al padre.
     * No procesa nada por sí mismo — solo recolecta y entrega.
     * El form "Por capas" solo se consulta si el switch layeredDesign está activo.
     */
    handleClick() {
      const defaultDesign = this.$refs.frm_default.getDataForm()
      // Si la validación falló, el form ya disparó el $toast.
      if (defaultDesign == null) return

      const cadLayersDesign = (this.layeredDesign && this.$refs.refLayersDesign)
        ? this.$refs.refLayersDesign.getDataDesign()
        : {}

      const cadLayeredDesign = Object.assign({}, { default: defaultDesign }, cadLayersDesign)

      this.$emit('epc-apply', {
        cadLayeredDesign,
        createElements: this.createElements,
      })

      this.hide()
    },

    /**
     * Abre el modal. Recibe el cadJson completo y extrae lo que necesita:
     *   - layers para <layers-design-list>
     *   - totalStructures + structuresByLayer (count de POLYLINE/LWPOLYLINE) para el resumen
     * @param {Object} cadJson - resultado del parser DXF
     */
    show(cadJson, applied = {}) {
      this.layers = Object.values(cadJson?.tables?.layer?.layers || {})

      const polys = (cadJson?.entities || []).filter(
        e => e.type === 'POLYLINE' || e.type === 'LWPOLYLINE'
      )
      this.totalStructures = polys.length

      const byLayer = Object.create(null)
      for (const e of polys) {
        const l = e.layer || '—'
        byLayer[l] = (byLayer[l] || 0) + 1
      }
      this.structuresByLayer = Object.entries(byLayer)
        .map(([layer, count]) => ({ layer, count }))
        .sort((a, b) => b.count - a.count)

      // Rehidratación desde la config ya aplicada (si la hay). cadLayeredDesign =
      // { default, [capaSaneada]: design... }. Separamos general vs por-capa.
      const layered = (applied && applied.cadLayeredDesign) || null
      const perLayer = {}
      if (layered) {
        for (const k in layered) {
          if (k !== 'default') perLayer[k] = layered[k]
        }
      }
      this.createElements = (applied && applied.createElements)
        ? { ...applied.createElements }
        : { pilings: false, modules: false }
      this.layerConfigs  = perLayer
      this.layeredDesign = Object.keys(perLayer).length > 0
      this.activeTab = 0
      // Valores del form General a sembrar tras montar (default aplicado).
      this._pendingDefault = (layered && layered.default) || null
      this.isModalActive = true

      this.$nextTick(() => {
        if (this._pendingDefault && this.$refs.frm_default) {
          this.$refs.frm_default.setForm(this._pendingDefault)
          this._pendingDefault = null
        }
        // layers-design-list se siembra solo con :initial-design (perLayer).
      })
    },

    hide() {
      this.isModalActive = false
    },
  },
};
</script>

<style scoped>
  /* Oculta los botones (spinners) de los inputs numéricos — no cubierto por Tailwind. */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type="number"] {
    -moz-appearance: textfield; /* Firefox */
    appearance: textfield;      /* Estándar */
  }
</style>
